<script setup lang="ts">
/**
 * Selling-prices editor for Call entries, grouped by currency.
 * Each entry has 3 numeric fields and a nested `BuyCallPricesForm`.
 */
import { computed, onMounted, ref, watch } from 'vue';
import { DxButton } from 'devextreme-vue/button';
import { DxTextBox } from 'devextreme-vue/text-box';
import { DxNumberBox } from 'devextreme-vue/number-box';
import { LookupBox, showError } from '@botto/core';
import { getCountriesLookup, getOperatorsLookup } from '../../lookups';
import { getExchangeRate } from '../../api/services/currencies';
import { convertCallPriceUiEntries } from '../../api/price-conversion';
import {
  arrayToBuyPrices,
  buyPricesToArray,
  isCallBuyPricesEmpty,
  type BuyPriceEntry,
} from '../../api/normalizers';
import { fetchCurrencyOptions } from '../../api';
import type { ByCurrency, CallPriceEntry, CallPrices } from '../../api/types';
import TariffCurrencyTabs from '../../components/TariffCurrencyTabs.vue';
import BuyCallPricesForm from './BuyCallPricesForm.vue';

interface UiEntry {
  country_iso: string;
  operator_id: number | null;
  /** UI keeps a string; serialized as null when empty on save. */
  direction_id: string;
  prices: CallPrices;
  buyPriceEntries: BuyPriceEntry<CallPrices>[];
}

const props = defineProps<{
  modelValue: ByCurrency<CallPriceEntry> | undefined;
}>();
const emit = defineEmits<{
  (e: 'update:modelValue', v: ByCurrency<CallPriceEntry>): void;
}>();

const currencies = ref<string[]>([]);
const currentCurrency = ref('');
const entriesByCurrency = ref<Record<string, UiEntry[]>>({});
const availableCurrencyOptions = ref<{ value: string; text: string }[]>([]);
const addingCurrency = ref(false);

const countriesLookup = getCountriesLookup();
const operatorsLookup = getOperatorsLookup();

onMounted(async () => {
  try {
    availableCurrencyOptions.value = await fetchCurrencyOptions();
  } catch (e) {
    showError(e);
  }
});

watch(
  () => props.modelValue,
  (v) => {
    const next: Record<string, UiEntry[]> = {};
    const fromApi = v ?? {};
    const presentCurrencies = [
      ...new Set([...Object.keys(fromApi), ...currencies.value]),
    ].sort((a, b) => a.localeCompare(b));
    for (const cur of presentCurrencies) {
      if (cur in fromApi) {
        next[cur] = (fromApi[cur] ?? []).map<UiEntry>((entry) => ({
          country_iso: entry.country_iso ?? '',
          operator_id: entry.operator_id && entry.operator_id > 0 ? entry.operator_id : null,
          direction_id: entry.direction_id ?? '',
          prices: {
            phone_rent: entry.prices?.phone_rent ?? 0,
            income_calls: entry.prices?.income_calls ?? 0,
            outcome_calls: entry.prices?.outcome_calls ?? 0,
          },
          buyPriceEntries: buyPricesToArray(entry.buy_prices),
        }));
      } else {
        next[cur] = entriesByCurrency.value[cur] ?? [];
      }
    }
    currencies.value = presentCurrencies;
    entriesByCurrency.value = next;
    if (!currentCurrency.value || !presentCurrencies.includes(currentCurrency.value)) {
      currentCurrency.value = presentCurrencies[0] ?? '';
    }
  },
  { immediate: true, deep: true },
);

const currentEntries = computed(() =>
  currentCurrency.value ? (entriesByCurrency.value[currentCurrency.value] ?? []) : [],
);

function pushUpdate(): void {
  const out: ByCurrency<CallPriceEntry> = {};
  for (const cur of currencies.value) {
    const list = entriesByCurrency.value[cur] ?? [];
    if (list.length === 0) {
      out[cur] = [];
      continue;
    }
    out[cur] = list.map<CallPriceEntry>((ui) => ({
      country_iso: ui.country_iso,
      operator_id: ui.operator_id && ui.operator_id > 0 ? ui.operator_id : null,
      direction_id: ui.direction_id || null,
      prices: ui.prices,
      buy_prices: arrayToBuyPrices(ui.buyPriceEntries, isCallBuyPricesEmpty),
    }));
  }
  emit('update:modelValue', out);
}

async function onAddCurrency(payload: { currency: string; copyFrom?: string }): Promise<void> {
  if (currencies.value.includes(payload.currency)) return;
  addingCurrency.value = true;
  try {
    let entries: UiEntry[] = [];
    if (payload.copyFrom) {
      const source = entriesByCurrency.value[payload.copyFrom] ?? [];
      const rate = await getExchangeRate(payload.copyFrom, payload.currency);
      entries = convertCallPriceUiEntries(source, rate);
    }
    entriesByCurrency.value[payload.currency] = entries;
    currencies.value = [...currencies.value, payload.currency].sort((a, b) =>
      a.localeCompare(b),
    );
    currentCurrency.value = payload.currency;
    pushUpdate();
  } catch (e) {
    showError(e);
  } finally {
    addingCurrency.value = false;
  }
}

function addEntry(): void {
  if (!currentCurrency.value) return;
  const list = entriesByCurrency.value[currentCurrency.value] ?? [];
  const newEntry: UiEntry = {
    country_iso: '',
    operator_id: null,
    direction_id: '',
    prices: { phone_rent: 0, income_calls: 0, outcome_calls: 0 },
    buyPriceEntries: [],
  };
  list.push(newEntry);
  entriesByCurrency.value[currentCurrency.value] = list;
  pushUpdate();
}

function removeEntry(index: number): void {
  const list = entriesByCurrency.value[currentCurrency.value] ?? [];
  list.splice(index, 1);
  pushUpdate();
}

function onBuyPricesUpdate(index: number, next: BuyPriceEntry<CallPrices>[]): void {
  const list = entriesByCurrency.value[currentCurrency.value];
  if (!list || !list[index]) return;
  list[index].buyPriceEntries = next;
  pushUpdate();
}
</script>

<template>
  <div class="calls-prices-editor">
    <div class="cpe-toolbar">
      <TariffCurrencyTabs
        :currencies="currencies"
        :current-currency="currentCurrency"
        :available-currency-options="availableCurrencyOptions"
        :adding="addingCurrency"
        @update:current-currency="currentCurrency = $event"
        @add="onAddCurrency"
      />
      <DxButton
        icon="add"
        text="Добавить строку"
        type="default"
        styling-mode="contained"
        :disabled="!currentCurrency"
        @click="addEntry"
      />
    </div>

    <div v-if="!currentCurrency" class="cpe-empty">
      Добавьте валюту, чтобы настроить цены звонков.
    </div>

    <div v-else-if="currentEntries.length === 0" class="cpe-empty">
      Цены звонков для {{ currentCurrency }} пока не заданы.
    </div>

    <div
      v-for="(entry, idx) in currentEntries"
      :key="`${currentCurrency}-${idx}`"
      class="cpe-entry"
    >
      <div class="cpe-entry-header">
        <div class="cpe-fields">
          <label>
            <span>Страна</span>
            <LookupBox v-model="entry.country_iso" :lookup="countriesLookup" placeholder="Страна" />
          </label>
          <label>
            <span>Оператор</span>
            <LookupBox v-model="entry.operator_id" :lookup="operatorsLookup" placeholder="Оператор" />
          </label>
          <label>
            <span>Direction ID</span>
            <DxTextBox v-model:value="entry.direction_id" placeholder="напр. 250 01" @value-changed="pushUpdate" />
          </label>
        </div>
        <DxButton icon="trash" hint="Удалить строку" styling-mode="text" @click="removeEntry(idx)" />
      </div>

      <div class="cpe-section">
        <div class="cpe-section-title">Цены продажи</div>
        <div class="cpe-prices">
          <label>
            <span>Аренда номера</span>
            <DxNumberBox v-model:value="entry.prices.phone_rent" @value-changed="pushUpdate" />
          </label>
          <label>
            <span>Входящие</span>
            <DxNumberBox v-model:value="entry.prices.income_calls" @value-changed="pushUpdate" />
          </label>
          <label>
            <span>Исходящие</span>
            <DxNumberBox v-model:value="entry.prices.outcome_calls" @value-changed="pushUpdate" />
          </label>
        </div>
      </div>

      <div class="cpe-section">
        <div class="cpe-section-title">Закупочные цены по агрегаторам</div>
        <BuyCallPricesForm
          :model-value="entry.buyPriceEntries"
          @update:model-value="(v) => onBuyPricesUpdate(idx, v)"
        />
      </div>
    </div>
  </div>
</template>

<style scoped>
.calls-prices-editor { display: flex; flex-direction: column; gap: 16px; padding-top: 8px; }
.cpe-toolbar { display: flex; align-items: center; justify-content: space-between; gap: 12px; flex-wrap: wrap; }
.cpe-empty { color: #9ca3af; padding: 24px; text-align: center; border: 1px dashed #e5e7eb; border-radius: 4px; }
.cpe-entry { border: 1px solid #e5e7eb; border-radius: 6px; padding: 12px; }
.cpe-entry-header { display: flex; gap: 12px; align-items: flex-start; justify-content: space-between; }
.cpe-fields { display: flex; gap: 12px; flex-wrap: wrap; flex: 1; }
.cpe-fields label, .cpe-prices label { display: flex; flex-direction: column; gap: 4px; font-size: 12px; color: #555; min-width: 180px; }
.cpe-section { margin-top: 12px; }
.cpe-section-title { font-weight: 500; margin-bottom: 6px; }
.cpe-prices { display: flex; gap: 16px; flex-wrap: wrap; }
</style>
